import { useEffect, useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Search, UserCheck, Loader2 } from 'lucide-react';
import { clientsApi, adminApi } from '../services/api';
import { toast } from 'sonner';

/**
 * Dialog to manage which workers/supervisors are persistently assigned to a client.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - client: { id, name, assigned_worker_ids? }  — the client to edit
 *  - onSaved: () => void                          — callback after successful save
 */
export function AssignWorkersDialog({ open, onOpenChange, client, onSaved }) {
  const [allUsers, setAllUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setSelected(new Set(client.assigned_worker_ids || []));
    setSearch('');
    setLoading(true);
    adminApi.getUsers()
      .then((res) => {
        const eligible = (res.data || []).filter(u => ['worker', 'supervisor'].includes(u.role));
        setAllUsers(eligible);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load workers'))
      .finally(() => setLoading(false));
  }, [open, client]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u =>
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q)
    );
  }, [allUsers, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await clientsApi.setAssignedWorkers(client.id, Array.from(selected));
      toast.success(`Saved — ${selected.size} worker${selected.size !== 1 ? 's' : ''} assigned to ${client.name}`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-workers-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-teal-600" />
            Assign workers to {client?.name}
          </DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Tick the workers who regularly support this participant. They'll see this client in their own list
            and appear pinned in Team Allocations even without a rostered shift.
          </p>
        </DialogHeader>

        <div className="relative my-3">
          <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search workers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="assign-workers-search"
          />
        </div>

        <div className="max-h-80 overflow-y-auto border rounded-md divide-y" data-testid="assign-workers-list">
          {loading ? (
            <div className="py-8 text-center text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading workers...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-slate-400">No workers found.</div>
          ) : (
            filtered.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 p-2.5 hover:bg-slate-50 cursor-pointer"
                data-testid={`assign-worker-row-${u.id}`}
              >
                <Checkbox
                  checked={selected.has(u.id)}
                  onCheckedChange={() => toggle(u.id)}
                  data-testid={`assign-worker-checkbox-${u.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-800 truncate">{u.name}</span>
                    <Badge variant="outline" className="text-[10px] uppercase">{u.role}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
              </label>
            ))
          )}
        </div>

        <div className="text-xs text-slate-500 mt-1">
          {selected.size} selected
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-teal-600 hover:bg-teal-700" data-testid="assign-workers-save-btn">
            {saving ? 'Saving...' : 'Save assignments'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
